import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-hot-toast";
import { getVendorName } from "../../../utils/getVendorName";
import {
  useFetchEcommerceOrderByIdMutation,
  useUpdateEcommerceOrderMutation,
  useFetchEcommerceOrderLogsMutation,
  useReportEcommerceShortfallMutation,
  useShipEcommerceOrderMutation,
  useDeliverEcommerceOrderMutation,
} from "../../../features/tenant/ecommerceOrder/ecommerceOrderApi";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import ModalComponent from "../../../components/Tenant/ModalComponent/ModalComponent";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import {
  FiArrowLeft,
  FiCheckCircle,
  FiXCircle,
  FiEdit,
  FiTruck,
  FiPackage,
  FiMessageSquare,
  FiActivity,
  FiAlertTriangle,
} from "react-icons/fi";

const ECOMMERCE_ORDER_STATUS = {
  REJECTED: "0",
  PENDING: "2",
  SHORTFALL_AWAITING_USER: "3",
  SHORTFALL_AUTO_APPROVED: "4",
  APPROVED: "5",
  IN_TRANSIT: "6",
  DELIVERED: "7",
  DISTRIBUTOR_PENDING: "8",
};

const getStatusInfo = (status) => {
  const s = status?.toString();
  switch (s) {
    case "0": return { label: "Rejected",                  colorClass: "bg-red-100 text-red-700 border border-red-200" };
    case "2": return { label: "Pending",                   colorClass: "bg-yellow-100 text-yellow-700 border border-yellow-200" };
    case "3": return { label: "Shortfall – Awaiting User", colorClass: "bg-purple-100 text-purple-700 border border-purple-200" };
    case "4": return { label: "Shortfall Auto-Approved",   colorClass: "bg-indigo-100 text-indigo-700 border border-indigo-200" };
    case "5": return { label: "Approved",                  colorClass: "bg-green-100 text-green-700 border border-green-200" };
    case "6": return { label: "In Transit",                colorClass: "bg-blue-100 text-blue-700 border border-blue-200" };
    case "7": return { label: "Delivered",                 colorClass: "bg-teal-100 text-teal-700 border border-teal-200" };
    case "8": return { label: "Distributor Pending",        colorClass: "bg-orange-100 text-orange-700 border border-orange-200" };
    default:  return { label: `Unknown (${s})`,            colorClass: "bg-gray-100 text-gray-700 border border-gray-200" };
  }
};

const EcommerceOrderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const vendorData = JSON.parse(sessionStorage.getItem("vendorData"));
  const tenant_id = vendorData?.tenant_id;
  const token = sessionStorage.getItem("access_token");

  // Data
  const [orderDetails, setOrderDetails] = useState([]);
  const [orderLogs, setOrderLogs] = useState([]);

   const imagesByColor = useMemo(() => {
    const map = {};
    (orderDetails[0]?.images || []).forEach((entry) => {
      const key = (entry.color_name || "").trim().toUpperCase();
      if (key) {
        map[key] = {
          colorId: entry.color_id,
          urls: entry.urls || [],
        };
      }
    });
    return map;
  }, [orderDetails]);

  const getColorImage = (colorName) => {
    const key = (colorName || "").trim().toUpperCase();
    return imagesByColor[key]?.urls?.[0] || null;
  };

  // Action modal
  const [actionModal, setActionModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const [actionRemark, setActionRemark] = useState("");

  // Shortfall
  const [editShortfallMode, setEditShortfallMode] = useState(false);
  const [shortfallLines, setShortfallLines] = useState([]);
  const [shortfallType, setShortfallType] = useState("user_approve");
  const [shortfallRemarks, setShortfallRemarks] = useState("");

  // API
  const [fetchOrderById, { isLoading: isFetchingDetails }] = useFetchEcommerceOrderByIdMutation();
  const [fetchLogs, { isLoading: isFetchingLogs }] = useFetchEcommerceOrderLogsMutation();
  const [updateOrder, { isLoading: isUpdatingStatus }] = useUpdateEcommerceOrderMutation();
  const [reportShortfall, { isLoading: isSubmittingShortfall }] = useReportEcommerceShortfallMutation();
  const [shipOrder, { isLoading: isShipping }] = useShipEcommerceOrderMutation();
  const [deliverOrder, { isLoading: isDelivering }] = useDeliverEcommerceOrderMutation();

  const loadOrderDetail = () => {
    if (!id || !tenant_id) return;

    fetchOrderById({ slug: tenant_id, token, order_id: id.toString() })
      .unwrap()
      .then((res) => {
        const orderData = res?.body?.orderData || res?.orderData || (Array.isArray(res) ? res : []);
        if (orderData && orderData.length > 0) {
          setOrderDetails(orderData);
        } else {
          toast.error("No details found for this order");
        }
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to load order details");
      });

    fetchLogs({ slug: tenant_id, token, order_id: id.toString() })
      .unwrap()
      .then((res) => {
        setOrderLogs(res?.body?.logs || res?.logs || []);
      })
      .catch((err) => {
        console.warn("Failed to load logs:", err);
      });
  };

  useEffect(() => {
    loadOrderDetail();
  }, [id, tenant_id]);

  const handleOpenActionModal = (type) => {
    setActionType(type);
    setActionRemark("");
    setActionModal(true);
  };

  const handleActionSubmit = () => {
    if (
      (actionType === "REJECTED" || actionType === "SHIP" || actionType === "DELIVER") &&
      !actionRemark
    ) {
      toast.error("Please provide a remark for this action");
      return;
    }

    const payload = {
      slug: tenant_id,
      token,
      order_id: Number(id),
      remarks: actionRemark,
      remark: actionRemark,
    };

    let apiCall;
    if (actionType === "APPROVED" || actionType === "REJECTED") {
      apiCall = updateOrder({ ...payload, status: actionType });
    } else if (actionType === "SHIP") {
      apiCall = shipOrder(payload);
    } else if (actionType === "DELIVER") {
      apiCall = deliverOrder(payload);
    }

    if (!apiCall) return;

    apiCall
      .unwrap()
      .then(() => {
        toast.success(`Order ${actionType.toLowerCase()} successfully`);
        setActionModal(false);
        loadOrderDetail();
      })
      .catch((err) => {
        toast.error(err?.data?.message || `Failed: ${actionType}`);
      });
  };

  const handleToggleShortfallMode = () => {
    if (editShortfallMode) {
      setEditShortfallMode(false);
    } else {
      const lines = orderDetails.map((line) => {
        const qty = Number(line.order_qty || 0);
        const amt = Number(line.order_purchase_amount || 0);
        const unitPrice = qty > 0 ? amt / qty : 0;
        return { ...line, newQty: qty, unitPrice, newAmount: amt };
      });
      setShortfallLines(lines);
      setShortfallRemarks("");
      setShortfallType("user_approve");
      setEditShortfallMode(true);
    }
  };

  const handleQtyChange = (index, value) => {
    const updated = [...shortfallLines];
    const qty = Math.max(0, parseInt(value) || 0);
    updated[index].newQty = qty;
    updated[index].newAmount = Number((qty * updated[index].unitPrice).toFixed(2));
    setShortfallLines(updated);
  };

  const handleShortfallSubmit = () => {
    if (!shortfallRemarks) {
      toast.error("Please provide remarks for reporting shortfall");
      return;
    }

    reportShortfall({
      slug: tenant_id,
      token,
      order_id: Number(id),
      shortfall_type: shortfallType,
      remarks: shortfallRemarks,
      updated_lines: shortfallLines.map((line) => ({
        article_no: line.article_no,
        qty: line.newQty,
        purchase_amount: line.newAmount,
        color: line.color,
        size: line.size,
      })),
    })
      .unwrap()
      .then(() => {
        toast.success("Shortfall reported successfully");
        setEditShortfallMode(false);
        loadOrderDetail();
      })
      .catch((err) => {
        toast.error(err?.data?.message || "Failed to submit shortfall");
      });
  };

  // ── Loading ──
  if (isFetchingDetails && orderDetails.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-100 gap-3">
        <CircleLoaderComponent />
        <p className="text-sm font-semibold text-gray-500">Loading order details...</p>
      </div>
    );
  }

  // ── Empty ──
  if (orderDetails.length === 0) {
    return (
      <div className="flex flex-col w-full bg-gray-100 min-h-screen">
        <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(`/genefied/${getVendorName()}/ecommerce-orders`)}
              className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <FiArrowLeft size={18} />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-800 tracking-tight">Order Detail</h1>
              <LinksComponent />
            </div>
          </div>
        </div>
        <div className="flex flex-col items-center justify-center flex-1 p-6">
          <div className="bg-white rounded-xl shadow-lg border border-gray-100 p-10 text-center max-w-sm w-full">
            <FiXCircle className="mx-auto text-red-400 w-12 h-12 mb-4" />
            <h2 className="text-lg font-bold text-gray-800">Order Not Found</h2>
            <p className="text-sm text-gray-500 mt-2 mb-6">
              The order details could not be retrieved.
            </p>
            <button
              onClick={() => navigate(`/genefied/${getVendorName()}/ecommerce-orders`)}
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-700 hover:bg-blue-800 text-white text-sm font-medium rounded-lg transition-colors"
            >
              <FiArrowLeft size={15} /> Back to Orders
            </button>
          </div>
        </div>
      </div>
    );
  }

  const orderMaster = orderDetails[0];
  const statusStr = orderMaster.status?.toString();
  const isPending  = statusStr === ECOMMERCE_ORDER_STATUS.PENDING;
  const isApproved = statusStr === ECOMMERCE_ORDER_STATUS.APPROVED;
  const isTransit  = statusStr === ECOMMERCE_ORDER_STATUS.IN_TRANSIT;
  const isUnmapped = !orderMaster.seller_app_user_id || Number(orderMaster.seller_app_user_id) === 0;
  const showAdminActions = isUnmapped || statusStr !== ECOMMERCE_ORDER_STATUS.DISTRIBUTOR_PENDING;

  const { label: statusLabel, colorClass: statusColor } = getStatusInfo(orderMaster.status);

  const shortfallTotalQty    = shortfallLines.reduce((a, c) => a + c.newQty, 0);
  const shortfallTotalAmount = shortfallLines.reduce((a, c) => a + c.newAmount, 0);

  const isActionBusy = isUpdatingStatus || isShipping || isDelivering;

  return (
    <>
      <div className="flex flex-col w-full bg-gray-100 min-h-screen">

        {/* ── Page Header ── */}
        <div className="bg-white border-b border-gray-200 px-6 py-4 shadow-sm">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate(`/genefied/${getVendorName()}/ecommerce-orders`)}
                className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg transition-colors"
                title="Back to Orders"
              >
                <FiArrowLeft size={18} />
              </button>
              <div>
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-2xl font-bold text-gray-800 tracking-tight">
                    Order #{orderMaster.voucher_no || id}
                  </h1>
                  <span className={`px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${statusColor}`}>
                    {statusLabel}
                  </span>
                  {editShortfallMode && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200 whitespace-nowrap">
                      Shortfall Edit Mode
                    </span>
                  )}
                </div>
                <LinksComponent />
              </div>
            </div>
          </div>
        </div>

        {/* ── Main ── */}
        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

            {/* ══ Left column ══ */}
            <div className="lg:col-span-2 space-y-6">

              {/* Mapped warning */}
              {!isUnmapped && (
                <div className="flex items-start gap-3 bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
                  <FiAlertTriangle size={16} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                  <p className="text-sm text-yellow-800">
                    <span className="font-bold">Mapped Order:</span> Assigned to distributor{" "}
                    <strong>#{orderMaster.seller_app_user_id}</strong>. Actions must be taken by the distributor directly.
                  </p>
                </div>
              )}

              {/* Order Info */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-base font-bold text-gray-800">Order Information</h2>
                </div>
                <div className="p-6 grid grid-cols-2 sm:grid-cols-3 gap-x-6 gap-y-5">
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Customer</p>
                    <p className="text-sm font-semibold text-gray-800">{orderMaster.user_name || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Mobile</p>
                    <p className="text-sm font-semibold text-gray-800">{orderMaster.user_mobile || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">User Type / Tier</p>
                    <p className="text-sm font-semibold text-gray-800">{orderMaster.user_type || "—"}</p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Order Date</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {orderMaster.created_at
                        ? new Date(orderMaster.created_at).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" })
                        : "—"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Expected Delivery</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {orderMaster.expected_delivery
                        ? new Date(orderMaster.expected_delivery).toLocaleDateString(undefined, { dateStyle: "medium" })
                        : "Not specified"}
                    </p>
                  </div>
                  <div>
                    <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-1">Created By</p>
                    <p className="text-sm font-semibold text-gray-800">
                      {orderMaster.created_by_name || orderMaster.created_user_name || "—"}
                      {(orderMaster.created_by_type) && (
                        <span className="text-gray-400 font-normal ml-1">({orderMaster.created_by_type})</span>
                      )}
                    </p>
                  </div>
                </div>

                {/* Remark */}
                {orderMaster.remark && (
                  <div className="mx-6 mb-6 flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3">
                    <FiMessageSquare size={14} className="text-amber-600 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs font-bold text-amber-700 uppercase tracking-wide mb-0.5">Order Remark</p>
                      <p className="text-sm text-amber-900">"{orderMaster.remark}"</p>
                    </div>
                  </div>
                )}
              </div>

              {/* Items Table */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                  <h2 className="text-base font-bold text-gray-800">
                    {editShortfallMode ? "Order Items — Edit Quantities" : "Order Items"}
                  </h2>
                  {editShortfallMode && (
                    <span className="px-3 py-1 rounded-full text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">
                      Editing
                    </span>
                  )}
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-100 text-xs font-bold text-gray-500 uppercase tracking-wider">
                        <th className="px-6 py-3">Article No</th>
                        <th className="px-6 py-3">Color</th>
                        <th className="px-6 py-3">Size</th>
                        <th className="px-6 py-3 text-center w-24">Orig. Qty</th>
                        <th className="px-6 py-3 text-center w-32">{editShortfallMode ? "New Qty" : "Qty"}</th>
                        <th className="px-6 py-3 text-right">Unit Price</th>
                        <th className="px-6 py-3 text-right">Line Total</th>
                        <th className="px-6 py-3">Image</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {editShortfallMode
                        ? shortfallLines.map((line, idx) => (
                            <tr key={line.id || idx} className="hover:bg-gray-50 transition-colors">
                              <td className="px-6 py-3 font-semibold text-gray-900">{line.article_no}</td>
                              <td className="px-6 py-3 text-gray-600">{line.color || "—"}</td>
                              <td className="px-6 py-3 text-gray-600">{line.size || "—"}</td>
                              <td className="px-6 py-3 text-center text-gray-400 font-medium">{line.order_qty}</td>
                              <td className="px-6 py-3 text-center">
                                <input
                                  type="number"
                                  min="0"
                                  style={{ width: "80px", minWidth: "80px" }}
                                  className="px-2 py-1.5 bg-white border-2 border-purple-300 rounded-lg outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-100 text-sm font-semibold text-center text-gray-800 block mx-auto"
                                  value={line.newQty}
                                  onChange={(e) => handleQtyChange(idx, e.target.value)}
                                />
                              </td>
                              <td className="px-6 py-3 text-right text-gray-600">₹{Number(line.unitPrice).toFixed(2)}</td>
                              <td className="px-6 py-3 text-right font-bold text-blue-700">₹{Number(line.newAmount).toFixed(2)}</td>
                              <td className="px-6 py-3">
                                {getColorImage(line.color) ? (
                                  <img
                                    src={getColorImage(line.color)}
                                    alt={line.color || "product"}
                                    className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                  />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-gray-100 border border-dashed border-gray-200 flex items-center justify-center text-[9px] text-gray-400">
                                    N/A
                                  </div>
                                )}
                              </td>
                            </tr>
                          ))
                        : orderDetails.map((line, idx) => {
                            const qty = Number(line.order_qty || 0);
                            const amt = Number(line.order_purchase_amount || 0);
                            const unitPrice = qty > 0 ? amt / qty : 0;
                            return (
                              <tr key={line.id || idx} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-3 font-semibold text-gray-900">{line.article_no}</td>
                                <td className="px-6 py-3 text-gray-600">{line.color || "—"}</td>
                                <td className="px-6 py-3 text-gray-600">{line.size || "—"}</td>
                                <td className="px-6 py-3 text-center text-gray-400 font-medium">{qty}</td>
                                <td className="px-6 py-3 text-center font-semibold text-gray-800">{qty}</td>
                                <td className="px-6 py-3 text-right text-gray-600">₹{unitPrice.toFixed(2)}</td>
                                <td className="px-6 py-3 text-right font-semibold text-gray-900">₹{amt.toFixed(2)}</td>
                                <td className="px-6 py-3">
                                  {getColorImage(line.color) ? (
                                    <img
                                      src={getColorImage(line.color)}
                                      alt={line.color || "product"}
                                      className="w-12 h-12 object-cover rounded-lg border border-gray-200"
                                    />
                                  ) : (
                                    <div className="w-12 h-12 rounded-lg bg-gray-100 border border-dashed border-gray-200 flex items-center justify-center text-[9px] text-gray-400">
                                      N/A
                                    </div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                    </tbody>
                  </table>
                </div>

                {/* Totals row */}
                <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex flex-wrap justify-end items-center gap-6">
                  <div className="flex items-center gap-2 text-sm">
                    <span className="text-gray-500 font-medium">Total Qty:</span>
                    <span className="font-bold text-gray-900">
                      {editShortfallMode ? shortfallTotalQty : (orderMaster.qty || 0)}
                    </span>
                  </div>
                  <div className="flex items-center gap-2 text-sm border-l border-gray-200 pl-6">
                    <span className="text-gray-500 font-medium">Grand Total:</span>
                    <span className="font-bold text-blue-700 text-base">
                      ₹{editShortfallMode
                        ? shortfallTotalAmount.toFixed(2)
                        : Number(orderMaster.bill_value || 0).toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shortfall Config Form */}
              {editShortfallMode && (
                <div className="bg-white rounded-xl shadow-lg border border-purple-200 overflow-visible">
                  <div className="px-6 py-4 border-b border-gray-100">
                    <h2 className="text-base font-bold text-gray-800">Shortfall Configuration</h2>
                    <p className="text-sm text-gray-500 mt-0.5">Choose approval route and provide a reason</p>
                  </div>
                  <div className="p-6 space-y-5">
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                        Shortfall Approval Type
                      </label>
                      <select
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-sm"
                        value={shortfallType}
                        onChange={(e) => setShortfallType(e.target.value)}
                      >
                        <option value="user_approve">Awaiting User Approval (Retailer)</option>
                        <option value="auto_approve">Auto-Approve (Distributor Direct)</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">
                        Remarks / Reason <span className="text-red-500">*</span>
                      </label>
                      <textarea
                        className="w-full px-4 py-2 bg-gray-50 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all outline-none text-sm resize-none"
                        rows={3}
                        placeholder="Reason for adjusting quantities..."
                        value={shortfallRemarks}
                        onChange={(e) => setShortfallRemarks(e.target.value)}
                      />
                    </div>
                    <div className="flex justify-end gap-3 pt-2 border-t border-gray-100">
                      <button
                        onClick={handleToggleShortfallMode}
                        className="px-5 py-2 text-sm font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-all uppercase tracking-widest"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleShortfallSubmit}
                        disabled={isSubmittingShortfall || shortfallLines.length === 0}
                        className="px-6 py-2 text-sm font-black text-white bg-purple-600 hover:bg-purple-700 rounded-lg transition-all disabled:opacity-60 disabled:cursor-not-allowed uppercase tracking-widest"
                      >
                        {isSubmittingShortfall ? "Saving..." : "Confirm Shortfall"}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ══ Right column ══ */}
            <div className="space-y-6">

              {/* Actions */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100">
                  <h2 className="text-base font-bold text-gray-800">Fulfillment Actions</h2>
                </div>
                <div className="p-4 space-y-3">
                  {showAdminActions ? (
                    <>
                      {isPending && !editShortfallMode && (
                        <>
                          <button
                            onClick={() => handleOpenActionModal("APPROVED")}
                            className="w-full py-2.5 text-sm font-bold text-white bg-green-600 hover:bg-green-700 focus:ring-4 focus:outline-none focus:ring-green-300 rounded-lg transition-colors"
                          >
                            ✓ Approve Order
                          </button>
                          <button
                            onClick={() => handleOpenActionModal("REJECTED")}
                            className="w-full py-2.5 text-sm font-bold text-red-700 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg transition-colors"
                          >
                            ✕ Reject Order
                          </button>
                          <button
                            onClick={handleToggleShortfallMode}
                            className="w-full py-2.5 text-sm font-bold text-purple-700 bg-purple-50 hover:bg-purple-100 border border-purple-200 rounded-lg transition-colors"
                          >
                            ↗ Report Shortfall
                          </button>
                        </>
                      )}

                      {isPending && editShortfallMode && (
                        <div className="text-center py-4 text-xs font-medium text-gray-500 bg-purple-50 border border-purple-100 rounded-lg">
                          Complete the shortfall form in the left panel.
                        </div>
                      )}

                      {isApproved && (
                        <button
                          onClick={() => handleOpenActionModal("SHIP")}
                          className="w-full py-2.5 text-sm font-bold text-white bg-blue-700 hover:bg-blue-800 focus:ring-4 focus:outline-none focus:ring-blue-300 rounded-lg transition-colors"
                        >
                          <FiTruck className="inline mr-1.5" size={14} /> Ship / Dispatch Order
                        </button>
                      )}

                      {isTransit && (
                        <button
                          onClick={() => handleOpenActionModal("DELIVER")}
                          className="w-full py-2.5 text-sm font-bold text-white bg-teal-600 hover:bg-teal-700 focus:ring-4 focus:outline-none focus:ring-teal-300 rounded-lg transition-colors"
                        >
                          <FiPackage className="inline mr-1.5" size={14} /> Mark as Delivered
                        </button>
                      )}

                      {!isPending && !isApproved && !isTransit && (
                        <div className="text-center py-4 text-xs font-medium text-gray-400 bg-gray-50 border border-gray-100 rounded-lg">
                          No actions available for current status.
                        </div>
                      )}
                    </>
                  ) : (
                    <div className="flex items-start gap-2 text-sm text-yellow-800 bg-yellow-50 border border-yellow-200 rounded-lg px-3 py-3">
                      <FiAlertTriangle size={14} className="text-yellow-600 mt-0.5 flex-shrink-0" />
                      <span>
                        Mapped to distributor{" "}
                        <strong>#{orderMaster.seller_app_user_id}</strong>. Awaiting distributor approval.
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {/* Audit Log */}
              <div className="bg-white rounded-xl shadow-lg border border-gray-100">
                <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
                  <FiActivity size={15} className="text-gray-500" />
                  <h2 className="text-base font-bold text-gray-800">Audit Log</h2>
                </div>
                <div className="max-h-[480px] overflow-y-auto">
                  {isFetchingLogs ? (
                    <div className="flex justify-center items-center py-10">
                      <CircleLoaderComponent />
                    </div>
                  ) : orderLogs.length > 0 ? (
                    <div className="p-4 space-y-3">
                      {orderLogs.map((log, idx) => {
                        const dateStr = log.created_at
                          ? new Date(log.created_at).toLocaleString(undefined, {
                              dateStyle: "short",
                              timeStyle: "short",
                            })
                          : "—";

                        const actionIncludes = (str) => log.action?.toUpperCase().includes(str);
                        const dotBg = actionIncludes("REJECT")
                          ? "bg-red-500"
                          : actionIncludes("APPROVE")
                          ? "bg-green-500"
                          : actionIncludes("SHORTFALL")
                          ? "bg-purple-500"
                          : actionIncludes("SHIP") || actionIncludes("TRANSIT")
                          ? "bg-blue-500"
                          : "bg-gray-400";

                        return (
                          <div
                            key={log.id || idx}
                            className="relative pl-5 pb-3 border-l-2 border-gray-100 last:border-transparent"
                          >
                            <span
                              className={`absolute -left-[5px] top-1.5 w-2.5 h-2.5 rounded-full border-2 border-white ${dotBg}`}
                            />
                            <div className="bg-gray-50 hover:bg-white border border-gray-100 hover:border-gray-200 rounded-xl p-3 transition-colors space-y-1.5">
                              <div className="flex items-center justify-between gap-2 flex-wrap">
                                <span className="text-xs font-bold text-gray-800 bg-white border border-gray-200 px-2 py-0.5 rounded uppercase tracking-wide">
                                  {log.action}
                                </span>
                                <span className="text-xs text-gray-400 font-medium whitespace-nowrap">{dateStr}</span>
                              </div>
                              <p className="text-xs text-gray-600">
                                By{" "}
                                <span className="font-semibold text-gray-800">{log.actor_name || "System"}</span>{" "}
                                <span className="text-gray-400 capitalize">({log.actor_type || "system"})</span>
                              </p>
                              {log.remarks && (
                                <p className="text-xs text-gray-600 italic bg-white px-2.5 py-1.5 rounded-lg border border-gray-100 leading-relaxed">
                                  "{log.remarks}"
                                </p>
                              )}
                              {log.status_from != null && log.status_to != null && (
                                <div className="flex items-center gap-1.5 flex-wrap">
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusInfo(log.status_from).colorClass}`}>
                                    {getStatusInfo(log.status_from).label}
                                  </span>
                                  <span className="text-gray-400 text-xs">→</span>
                                  <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${getStatusInfo(log.status_to).colorClass}`}>
                                    {getStatusInfo(log.status_to).label}
                                  </span>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="py-10 text-center text-sm text-gray-400 font-medium">
                      No audit records available
                    </div>
                  )}
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>

      {/* ── Action Confirmation Modal ── */}
      <ModalComponent show={actionModal} setShow={(val) => setActionModal(val)} size="md">
        <div className="p-8">
          <div className="flex items-center gap-4 mb-6">
            <div
              className={`p-4 rounded-2xl ${
                actionType === "APPROVED" ? "bg-green-100 text-green-600"
                : actionType === "REJECTED" ? "bg-red-100 text-red-600"
                : "bg-blue-100 text-blue-600"
              }`}
            >
              {actionType === "APPROVED" ? <FiCheckCircle size={32} />
               : actionType === "REJECTED" ? <FiXCircle size={32} />
               : actionType === "SHIP" ? <FiTruck size={32} />
               : <FiPackage size={32} />}
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900">
                {actionType === "APPROVED" ? "Approve Order"
                 : actionType === "REJECTED" ? "Reject Order"
                 : actionType === "SHIP" ? "Dispatch Order"
                 : "Mark as Delivered"}
              </h2>
              <p className="text-sm font-medium text-gray-500 mt-0.5">
                Order #{orderMaster.voucher_no || id} · This action is irreversible.
              </p>
            </div>
          </div>

          <div className="mb-8">
            <label className="block text-xs font-black text-gray-400 uppercase tracking-widest mb-2">
              Remarks / Notes{" "}
              {(actionType === "REJECTED" || actionType === "SHIP" || actionType === "DELIVER") && (
                <span className="text-red-500">*</span>
              )}
            </label>
            <textarea
              className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:bg-white outline-none transition-all text-sm font-medium min-h-[100px]"
              placeholder="Provide comments or notes for this action..."
              value={actionRemark}
              onChange={(e) => setActionRemark(e.target.value)}
            />
          </div>

          <div className="flex justify-end gap-4">
            <button
              onClick={() => setActionModal(false)}
              className="px-6 py-3 text-sm font-bold text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-xl transition-all uppercase tracking-widest"
            >
              Cancel
            </button>
            <button
              onClick={handleActionSubmit}
              disabled={isActionBusy}
              className={`px-8 py-3 text-sm font-black text-white rounded-xl transition-all shadow-lg active:scale-95 uppercase tracking-widest disabled:opacity-60 disabled:cursor-not-allowed ${
                actionType === "APPROVED" ? "bg-green-600 hover:bg-green-700 shadow-green-100"
                : actionType === "REJECTED" ? "bg-red-600 hover:bg-red-700 shadow-red-100"
                : actionType === "SHIP" ? "bg-blue-700 hover:bg-blue-800 shadow-blue-100"
                : "bg-teal-600 hover:bg-teal-700 shadow-teal-100"
              }`}
            >
              {isActionBusy
                ? "Processing..."
                : actionType === "APPROVED" ? "Confirm Approval"
                : actionType === "REJECTED" ? "Confirm Rejection"
                : "Confirm"}
            </button>
          </div>
        </div>
      </ModalComponent>
    </>
  );
};

export default EcommerceOrderDetail;
